import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CATEGORIES, findTool } from '../data/calculators';
import { CALC_REGISTRY } from '../calculators/registry';
import * as Icons from 'lucide-react';
import { ArrowRight, Star, Clock, BookOpen } from 'lucide-react';
import HomeSciCalc from '../components/HomeSciCalc';
import ScientificCalc from '../calculators/math/ScientificCalc';
import AdSlot from '../components/AdSlot';
import wpPosts from '../data/blogPosts.json';
import { getGeneratedArticles } from '../data/blogLoader';

const colorMap = {
  blue: 'from-blue-500 to-blue-700 text-blue-600 bg-blue-50 border-blue-200',
  green: 'from-emerald-500 to-emerald-700 text-emerald-600 bg-emerald-50 border-emerald-200',
  purple: 'from-violet-500 to-violet-700 text-violet-600 bg-violet-50 border-violet-200',
  orange: 'from-orange-500 to-orange-600 text-orange-600 bg-orange-50 border-orange-200'
};

// Default "popular" list shown when a visitor hasn't opened anything yet
const DEFAULT_POPULAR = ['mortgage','loan','auto-loan','investment','retirement','credit-card','bmi','scientific','percent-off','tip','age','discount'];

function PopularCalcs() {
  const [tab, setTab] = useState('recent');
  const [recent, setRecent] = useState([]);
  const [popular, setPopular] = useState([]);
  useEffect(() => {
    try {
      const r = JSON.parse(localStorage.getItem('calc_recent') || '[]');
      setRecent(r);
      const p = JSON.parse(localStorage.getItem('calc_popular') || '{}');
      const sorted = Object.entries(p).sort((a,b)=>b[1]-a[1]).map(([s])=>s);
      setPopular(sorted);
    } catch {}
  }, []);
  const list = tab === 'recent' ? (recent.length ? recent : DEFAULT_POPULAR) : (popular.length ? popular : DEFAULT_POPULAR);
  const tools = list.map(slug => findTool(slug)).filter(Boolean).slice(0, 8);
  if (tools.length === 0) return null;
  return (
    <div className="mt-8 p-5 rounded-2xl bg-white/70 border border-slate-200 backdrop-blur">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wide">
          {tab === 'recent' ? <span className="flex items-center gap-2"><Clock className="w-4 h-4"/> Recently Used</span> : <span className="flex items-center gap-2"><Star className="w-4 h-4"/> Popular</span>}
        </h3>
        <div className="flex gap-1 text-xs">
          <button onClick={()=>setTab('recent')} className={`px-2.5 py-1 rounded-full ${tab==='recent'?'bg-blue-700 text-white':'text-slate-600 hover:bg-slate-100'}`} data-testid="home-tab-recent">Recent</button>
          <button onClick={()=>setTab('popular')} className={`px-2.5 py-1 rounded-full ${tab==='popular'?'bg-blue-700 text-white':'text-slate-600 hover:bg-slate-100'}`} data-testid="home-tab-popular">Popular</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tools.map(t => (
          <Link key={t.slug} to={`/calc/${t.slug}`} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white border border-slate-200 hover:border-blue-400 hover:shadow-sm transition text-xs font-medium text-slate-700" data-testid={`home-pop-${t.slug}`}>
            <img src={`/icons/${t.slug}.png`} alt="" onError={(e)=>{e.currentTarget.src='/icons/_fallback.png'}} className="w-7 h-7 object-contain flex-shrink-0 bg-slate-50 border border-slate-200 rounded p-0.5"/>
            <span className="line-clamp-2">{t.name.replace(' Calculator','')}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero + Sci Calc */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-14 items-start">
        <div className="lg:col-span-3">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">Pocket Calculator</h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl">
            A modern collection of financial, health, math, and everyday calculators — designed with clean interfaces, interactive diagrams, tables, and step‑by‑step explanations.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {CATEGORIES.map(cat => (
              <Link key={cat.id} to={`/category/${cat.id}`} className={`px-4 py-2 rounded-full text-sm font-medium border ${colorMap[cat.color].split(' ').slice(2).join(' ')} hover:shadow-sm`}>
                {cat.name.replace(' Calculators', '')} · {cat.tools.length}
              </Link>
            ))}
          </div>
          <PopularCalcs />
          <AdSlot slotId="home-below-recent" minH={120} className="mt-6" />
        </div>
        <div className="lg:col-span-2">
          <HomeSciCalc replacement={<ScientificCalc slug="scientific" hideHeader />} />
        </div>
      </section>

      {/* Categories */}
      <div className="space-y-14">
        {CATEGORIES.map(cat => {
          const Icon = Icons[cat.icon] || Icons.Calculator;
          const [gradFrom, gradTo, textCol, bgCol, borderCol] = colorMap[cat.color].split(' ');
          return (
            <section key={cat.id}>
              <div className="flex items-start justify-between mb-6 gap-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br ${gradFrom} ${gradTo} flex items-center justify-center shadow-md flex-shrink-0`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{cat.name}</h2>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1">{cat.description}</p>
                  </div>
                </div>
                <Link to={`/category/${cat.id}`} className={`hidden sm:flex items-center gap-1 text-sm font-medium ${textCol} hover:underline flex-shrink-0`}>
                  View all ({cat.tools.length}) <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                {cat.tools.slice(0, 12).map(tool => {
                  const isImpl = !!CALC_REGISTRY[tool.slug];
                  return (
                  <Link
                    key={tool.slug}
                    to={`/calc/${tool.slug}`}
                    className={`group px-3 sm:px-4 py-3 rounded-xl bg-white border ${borderCol} hover:shadow-md hover:border-blue-400 transition-all text-xs sm:text-sm font-medium text-slate-700 flex items-center gap-2 sm:gap-3`}
                    data-testid={`home-tool-${tool.slug}`}
                  >
                    <img
                      src={`/icons/${tool.slug}.png`}
                      alt=""
                      onError={(e) => { e.currentTarget.src = '/icons/_fallback.png'; }}
                      className="w-11 h-11 sm:w-12 sm:h-12 object-contain flex-shrink-0 bg-white border border-slate-200 rounded-lg p-1"
                    />
                    <span className="line-clamp-2 flex-1">{tool.name}</span>
                    <ArrowRight className={`w-4 h-4 ${textCol} opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`} />
                  </Link>
                  );
                })}
              </div>

              <div className="mt-4 text-right sm:hidden">
                <Link to={`/category/${cat.id}`} className={`text-sm font-medium ${textCol}`}>
                  View all {cat.tools.length} →
                </Link>
              </div>
            </section>
          );
        })}
      </div>

      {/* Latest from Blog */}
      <LatestBlogSection />
    </div>
  );
}

function LatestBlogSection() {
  const posts = useMemo(() => {
    const all = [...getGeneratedArticles(), ...wpPosts];
    return all.sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 6);
  }, []);
  if (posts.length === 0) return null;
  return (
    <section className="mt-14" data-testid="home-blog-section">
      <div className="flex items-end justify-between mb-6">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-md">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Latest Guides &amp; Articles</h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">Learn how to use our calculators the smart way.</p>
          </div>
        </div>
        <Link to="/blog" className="hidden sm:flex items-center gap-1 text-sm font-medium text-rose-600 hover:underline">
          View all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map(p => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-rose-400 hover:shadow-sm transition"
            data-testid={`home-blog-${p.slug}`}
          >
            <div className="text-xs text-rose-600 font-semibold uppercase mb-2">{(p.categories && p.categories[0]) || 'Guide'}</div>
            <div className="font-semibold text-slate-900 mb-1 line-clamp-2">{p.title}</div>
            <div className="text-xs text-slate-500 mt-2">{(p.date || '').slice(0, 10)} · {Math.max(1, Math.round((p.wordCount || 1000) / 220))} min read</div>
          </Link>
        ))}
      </div>
      <div className="mt-4 text-right sm:hidden">
        <Link to="/blog" className="text-sm font-medium text-rose-600">View all articles →</Link>
      </div>
    </section>
  );
}
